/**
 * Data REST API Service
 * Handles paginated dataset access via REST endpoints
 */

// Types for pagination responses
export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface Dataset {
  id: number;
  name: string;
  description?: string;
  type: 'Images' | 'Metrics';
  imageCount?: number;
  size?: number;
  bucket?: string;
  dateCreated?: string;
  dateModified?: string;
  organizationId?: number;
}

export interface DatasetImage {
  id: number;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  url?: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  dateCreated?: string;
  metrics?: any;
  name?: string;
  fileAnnotations: any[];
}

export interface DatasetMetric {
  id: string; // BigInt converted to string
  createdAt: string;
  deviceId: number | null;
  metrics: any;
}

export interface DatasetFilters {
  type?: string;
  search?: string;
}

// Types for time-series metrics query
export interface MetricStats {
  min: number;
  max: number;
  avg: number;
  stdDev: number;
  count: number;
}

export interface MetricsTimeRangeParams {
  from?: string;      // ISO timestamp
  to?: string;        // ISO timestamp
  take?: number;      // Max records (default 5000, max 50000)
  aggregation?: string; // '1m' | '5m' | '15m' | '1h' | '6h' | '1d'
  metrics?: string[]; // Specific metrics to fetch
}

export interface MetricsTimeRangeResponse {
  status: number;
  data: DatasetMetric[];
  totalCount: number;
  timeRange: {
    from: string | null;
    to: string | null;
    actualFrom: string | null;
    actualTo: string | null;
  };
  aggregation: string | null;
  metricKeys: string[];
  stats: Record<string, MetricStats>;
}

// ==================== METRIC <-> BATCH LINKING ====================
export interface BatchOption {
  id: number;
  timelineId: number;
  mixCode: string;
  productName?: string | null;
  recipeCode?: string | null;
  sequence: number;
  mixerName?: string | null;
  ovenName?: string | null;
  batchStatus?: string | null;
  productionDate?: string | null;
  mixingStart?: string | null;
  bakingEnd?: string | null;
}

export interface BatchAssignment {
  id: number;
  datasetId: number;
  batchId: number;
  from: string;
  to: string;
  metricCount: number;
  note?: string | null;
  batch?: {
    id: number;
    mixCode: string;
    productName?: string | null;
    sequence: number;
    ovenName?: string | null;
  } | null;
}

export interface CreateBatchAssignmentPayload {
  batchId: number;
  from: string; // ISO timestamp
  to: string;   // ISO timestamp
  note?: string;
}

class DataApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = (process.env.REACT_APP_SOCKET_URL || 'http://localhost:5150') + '/api/data';
  }

  private getToken(): string {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.token || '';
    }
    return '';
  }

  private async request<T>(method: string, path: string, data?: any): Promise<T> {
    const token = this.getToken();

    const options: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    };

    if (data && (method === 'POST' || method === 'PUT')) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.baseUrl}${path}`, options);
    const json = await response.json();

    if (!response.ok || json.status >= 400) {
      throw new Error(json.error || json.message || 'Request failed');
    }

    return json;
  }

  // ==================== CREATE DATASET ====================
  async createDataset(name: string, type: string = 'Metrics', description?: string): Promise<Dataset> {
    const res = await this.request<{ status: number; data: Dataset }>(
      'POST',
      '/datasets',
      { name, type, description }
    );
    return res.data;
  }

  // ==================== DELETE DATASET ====================
  async deleteDataset(id: number): Promise<void> {
    await this.request<{ status: number }>('DELETE', `/datasets/${id}`);
  }

  // ==================== DATASETS LIST (PAGINATED) ====================
  async getDatasets(
    skip: number = 0,
    take: number = 50,
    filters?: DatasetFilters
  ): Promise<PaginatedResponse<Dataset>> {
    const params = new URLSearchParams();
    params.append('skip', String(skip));
    params.append('take', String(take));
    if (filters?.type) params.append('type', filters.type);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    return this.request<PaginatedResponse<Dataset>>('GET', `/datasets?${queryString}`);
  }

  // ==================== SINGLE DATASET METADATA ====================
  async getDataset(id: number): Promise<Dataset> {
    const response = await this.request<{ status: number; data: Dataset }>('GET', `/datasets/${id}`);
    return response.data;
  }

  // ==================== DATASET IMAGES (PAGINATED) ====================
  async getDatasetImages(
    datasetId: number,
    skip: number = 0,
    take: number = 50
  ): Promise<PaginatedResponse<DatasetImage>> {
    const params = new URLSearchParams();
    params.append('skip', String(skip));
    params.append('take', String(take));

    const queryString = params.toString();
    return this.request<PaginatedResponse<DatasetImage>>('GET', `/datasets/${datasetId}/images?${queryString}`);
  }

  // ==================== DATASET METRICS (PAGINATED) ====================
  async getDatasetMetrics(
    datasetId: number,
    skip: number = 0,
    take: number = 50
  ): Promise<PaginatedResponse<DatasetMetric>> {
    const params = new URLSearchParams();
    params.append('skip', String(skip));
    params.append('take', String(take));

    const queryString = params.toString();
    return this.request<PaginatedResponse<DatasetMetric>>('GET', `/datasets/${datasetId}/metrics?${queryString}`);
  }

  // ==================== DATASET METRICS (TIME-SERIES) ====================
  /**
   * Get metrics with time range, aggregation, and optional metric filtering.
   * This is the main API for the MetricsExplorer visualization component.
   */
  async getDatasetMetricsTimeRange(
    datasetId: number,
    params: MetricsTimeRangeParams = {}
  ): Promise<MetricsTimeRangeResponse> {
    const urlParams = new URLSearchParams();

    if (params.from) urlParams.append('from', params.from);
    if (params.to) urlParams.append('to', params.to);
    if (params.take) urlParams.append('take', String(params.take));
    if (params.aggregation && params.aggregation !== 'raw') {
      urlParams.append('aggregation', params.aggregation);
    }
    if (params.metrics && params.metrics.length > 0) {
      urlParams.append('metrics', params.metrics.join(','));
    }

    const queryString = urlParams.toString();
    return this.request<MetricsTimeRangeResponse>(
      'GET',
      `/datasets/${datasetId}/metrics${queryString ? '?' + queryString : ''}`
    );
  }

  // ==================== BATCH PICKER ====================
  /**
   * List production batches available to assign metrics to.
   */
  async getBatches(params: { date?: string; search?: string; take?: number } = {}): Promise<BatchOption[]> {
    const urlParams = new URLSearchParams();
    if (params.date) urlParams.append('date', params.date);
    if (params.search) urlParams.append('search', params.search);
    if (params.take) urlParams.append('take', String(params.take));

    const queryString = urlParams.toString();
    const res = await this.request<{ status: number; data: BatchOption[] }>(
      'GET',
      `/batches${queryString ? '?' + queryString : ''}`
    );
    return res.data;
  }

  /**
   * Get the time-series metrics linked to a production batch.
   */
  async getBatchMetrics(batchId: number, take?: number): Promise<MetricsTimeRangeResponse> {
    const urlParams = new URLSearchParams();
    if (take) urlParams.append('take', String(take));
    const queryString = urlParams.toString();
    return this.request<MetricsTimeRangeResponse>(
      'GET',
      `/batches/${batchId}/metrics${queryString ? '?' + queryString : ''}`
    );
  }

  // ==================== BATCH ASSIGNMENTS ====================
  /**
   * Get batch assignments for a dataset, optionally overlapping a time range.
   */
  async getBatchAssignments(
    datasetId: number,
    range?: { from?: string; to?: string }
  ): Promise<BatchAssignment[]> {
    const urlParams = new URLSearchParams();
    if (range?.from) urlParams.append('from', range.from);
    if (range?.to) urlParams.append('to', range.to);

    const queryString = urlParams.toString();
    const res = await this.request<{ status: number; data: BatchAssignment[] }>(
      'GET',
      `/datasets/${datasetId}/batch-assignments${queryString ? '?' + queryString : ''}`
    );
    return res.data;
  }

  /**
   * Assign all metrics of a dataset within [from, to] to a production batch.
   */
  async createBatchAssignment(
    datasetId: number,
    payload: CreateBatchAssignmentPayload
  ): Promise<{ assignment: any; metricCount: number }> {
    return this.request<{ assignment: any; metricCount: number }>(
      'POST',
      `/datasets/${datasetId}/batch-assignments`,
      payload
    );
  }

  /**
   * Remove a batch assignment and clear the relation on its metrics.
   */
  async deleteBatchAssignment(
    datasetId: number,
    assignmentId: number
  ): Promise<{ clearedCount: number }> {
    return this.request<{ clearedCount: number }>(
      'DELETE',
      `/datasets/${datasetId}/batch-assignments/${assignmentId}`
    );
  }
}

// Export singleton instance
const dataApi = new DataApi();
export default dataApi;
